import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import {
  GetPulseAvailability,
  GetLatestPulseScore,
  GetPulseScoreHistory
} from "graphql/pulse/pulse.gql";
import { derivePulseState } from "utils/derivePulseState";
import routesConfig from "utils/routesConfig";
import { Loading, Button, StyledP } from "components/simple";
import { color, fontFamily, fontSize, spacingDefaults } from "style/constants";

import PulseLandingLayout from "./PulseLandingLayout";

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingDefaults.normal};
  padding: ${spacingDefaults.xlarge} ${spacingDefaults.normal};
  text-align: center;
`;

const PulseLanding: React.FC = () => {
  const history = useHistory();

  const {
    data: availData,
    loading: availLoading,
    error: availError
  } = useQuery(GetPulseAvailability);

  const { data: scoreData, loading: scoreLoading } = useQuery(
    GetLatestPulseScore
  );

  const { data: historyData, loading: historyLoading } = useQuery(
    GetPulseScoreHistory
  );

  if (availLoading || scoreLoading || historyLoading) {
    return <Loading />;
  }

  if (availError) {
    return (
      <ErrorContainer data-testid="pulse-error">
        <StyledP
          color={color.DARKERGRAY}
          fontFamily={fontFamily.primary}
          fontSize={fontSize.normal}
        >
          Something went wrong loading your Pulse data.
        </StyledP>
        <Button
          onClick={() => window.location.reload()}
          data-testid="pulse-retry-button"
        >
          Try Again
        </Button>
      </ErrorContainer>
    );
  }

  const { lastCompletedAt, nextAvailableAt } =
    availData?.pulseAvailability ?? {};

  const pulseState = derivePulseState(
    lastCompletedAt ?? null,
    nextAvailableAt ?? null
  );

  const latestScore: number | null =
    scoreData?.latestPulseScore?.pulseIndex ?? null;

  const scoreHistory: Array<{ pulseIndex: number; calculatedAt: string }> =
    historyData?.pulseScoreHistory ?? [];

  const handleStartPulse = () => {
    history.push(routesConfig.pulse.questions.path);
  };

  return (
    <div data-testid="pulse-landing">
      <PulseLandingLayout
        score={latestScore ?? 0}
        nextPulseDate={pulseState.nextPulseDate}
        currentDay={pulseState.currentDay}
        totalDays={pulseState.totalDays}
        pulsesCompleted={scoreHistory.length}
        ctaEnabled={pulseState.state !== "cooldown"}
        onTakePulse={handleStartPulse}
        scoreHistory={scoreHistory}
      />
    </div>
  );
};

export default PulseLanding;
